/**
 * Animatic video export — turns the Watch-mode cut into a real video file, entirely
 * client-side. A canvas replays the cut (title card → boards with a gentle push-in and
 * read-along captions → end card) while MediaRecorder captures it; matched VO reads are
 * routed through WebAudio into the same recording. No engine calls, no spend.
 *
 * Recording is real time by design (captureStream), so a 90-second cut takes 90 seconds
 * to film. The UI says so.
 */

export interface AnimaticCaption {
  speaker?: string;
  text: string;
}

export interface AnimaticEntry {
  sceneId: string;
  src: string;
  durationSeconds: number;
  caption: AnimaticCaption | null;
}

export interface AnimaticScript {
  title: string;
  aspectRatio: string;
  entries: AnimaticEntry[];
  /** Ordered VO audio URLs per scene id — played back-to-back from the scene's first board. */
  voiceUrlsBySceneId: Record<string, string[]>;
}

export interface AnimaticProgress {
  elapsedSeconds: number;
  totalSeconds: number;
}

export interface AnimaticResult {
  blob: Blob;
  extension: "mp4" | "webm";
}

const TITLE_SECONDS = 2.4;
const END_SECONDS = 2.6;
const FPS = 30;

/** Same pacing clamp Watch mode uses, so the export matches what the director previewed. */
export function animaticEntrySeconds(durationSeconds: number | undefined): number {
  return Math.min(6, Math.max(1.6, durationSeconds || 3));
}

export function animaticTotalSeconds(script: AnimaticScript): number {
  return TITLE_SECONDS + script.entries.reduce((sum, entry) => sum + entry.durationSeconds, 0) + END_SECONDS;
}

function canvasSize(aspectRatio: string): { width: number; height: number } {
  switch (aspectRatio) {
    case "9:16":
      return { width: 1080, height: 1920 };
    case "1:1":
      return { width: 1080, height: 1080 };
    case "4:5":
      return { width: 1080, height: 1350 };
    case "2.39:1":
      return { width: 1920, height: 804 };
    default:
      return { width: 1920, height: 1080 };
  }
}

/** mp4 where the browser can mux it (Safari, newer Chrome); webm everywhere else. */
function pickMimeType(): { mimeType: string; extension: "mp4" | "webm" } | null {
  const candidates: Array<{ mimeType: string; extension: "mp4" | "webm" }> = [
    { mimeType: "video/mp4;codecs=avc1.42E01E,mp4a.40.2", extension: "mp4" },
    { mimeType: "video/mp4", extension: "mp4" },
    { mimeType: "video/webm;codecs=vp9,opus", extension: "webm" },
    { mimeType: "video/webm", extension: "webm" }
  ];
  for (const candidate of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(candidate.mimeType)) return candidate;
    } catch {
      // keep trying
    }
  }
  return null;
}

export function animaticSupported(): boolean {
  return typeof MediaRecorder !== "undefined" && pickMimeType() !== null;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

/** Cover-fit with a slow push-in — boards feel filmed instead of pasted. */
function drawBoard(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number,
  segmentProgress: number
) {
  const scale = 1 + 0.045 * Math.min(1, Math.max(0, segmentProgress));
  const cover = Math.max(width / image.width, height / image.height) * scale;
  const drawWidth = image.width * cover;
  const drawHeight = image.height * cover;
  context.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
}

function wrapLines(context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, 3);
}

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + w, y, x + w, y + h, r);
  context.arcTo(x + w, y + h, x, y + h, r);
  context.arcTo(x, y + h, x, y, r);
  context.arcTo(x, y, x + w, y, r);
  context.closePath();
}

function drawCaption(context: CanvasRenderingContext2D, caption: AnimaticCaption, width: number, height: number) {
  const base = Math.round(height * 0.026);
  const speakerSize = Math.round(base * 0.72);
  const padX = Math.round(base * 1.1);
  const padY = Math.round(base * 0.62);
  const maxWidth = width * 0.74;

  context.font = `600 ${base}px -apple-system, "SF Pro Text", "Helvetica Neue", Arial, sans-serif`;
  const lines = wrapLines(context, caption.text, maxWidth);
  const lineHeight = Math.round(base * 1.42);
  const textWidth = Math.max(
    ...lines.map((line) => context.measureText(line).width),
    caption.speaker ? context.measureText(caption.speaker.toUpperCase()).width : 0
  );
  const boxWidth = Math.min(maxWidth, textWidth) + padX * 2;
  const speakerBlock = caption.speaker ? speakerSize + Math.round(base * 0.35) : 0;
  const boxHeight = lines.length * lineHeight + speakerBlock + padY * 2;
  const x = (width - boxWidth) / 2;
  const y = height - boxHeight - Math.round(height * 0.075);

  context.fillStyle = "rgba(10, 12, 18, 0.62)";
  roundedRect(context, x, y, boxWidth, boxHeight, Math.round(base * 0.55));
  context.fill();

  context.textAlign = "center";
  context.textBaseline = "alphabetic";
  let cursorY = y + padY;
  if (caption.speaker) {
    context.fillStyle = "#9fc1ff";
    context.font = `800 ${speakerSize}px -apple-system, "SF Pro Text", "Helvetica Neue", Arial, sans-serif`;
    cursorY += speakerSize;
    context.fillText(caption.speaker.toUpperCase(), width / 2, cursorY);
    cursorY += Math.round(base * 0.35);
  }
  context.fillStyle = "#ffffff";
  context.font = `600 ${base}px -apple-system, "SF Pro Text", "Helvetica Neue", Arial, sans-serif`;
  for (const line of lines) {
    cursorY += lineHeight;
    context.fillText(line, width / 2, cursorY - Math.round(lineHeight * 0.28));
  }
}

function drawCard(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  title: string,
  eyebrow: string,
  isEnd: boolean
) {
  const gradient = context.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, "#101a2e");
  gradient.addColorStop(1, "#0b1322");
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);

  context.textAlign = "center";
  const eyebrowSize = Math.round(height * 0.02);
  const titleSize = Math.round(height * 0.062);
  context.fillStyle = "rgba(255, 255, 255, 0.62)";
  context.font = `800 ${eyebrowSize}px -apple-system, "SF Pro Text", "Helvetica Neue", Arial, sans-serif`;
  const eyebrowY = height / 2 + (isEnd ? titleSize : -titleSize) * 0.85;
  context.fillText(eyebrow.toUpperCase().split("").join(" "), width / 2, eyebrowY);
  context.fillStyle = "#ffffff";
  context.font = `800 ${titleSize}px -apple-system, "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;
  context.fillText(title, width / 2, height / 2 + titleSize * 0.32);
}

interface Segment {
  kind: "title" | "board" | "end";
  start: number;
  duration: number;
  entry?: AnimaticEntry;
  image?: HTMLImageElement | null;
}

export async function exportAnimatic(
  script: AnimaticScript,
  options: { onProgress?: (progress: AnimaticProgress) => void; signal?: AbortSignal } = {}
): Promise<AnimaticResult> {
  const picked = pickMimeType();
  if (!picked) throw new Error("This browser can't record video. Try Chrome or Safari.");
  if (!script.entries.length) throw new Error("No boards to film yet.");

  const { width, height } = canvasSize(script.aspectRatio);
  const totalSeconds = animaticTotalSeconds(script);

  // Preload every board before the clock starts.
  const images = await Promise.all(script.entries.map((entry) => loadImage(entry.src)));
  if (options.signal?.aborted) throw new DOMException("Cancelled", "AbortError");

  const segments: Segment[] = [{ kind: "title", start: 0, duration: TITLE_SECONDS }];
  let clock = TITLE_SECONDS;
  script.entries.forEach((entry, index) => {
    segments.push({ kind: "board", start: clock, duration: entry.durationSeconds, entry, image: images[index] });
    clock += entry.durationSeconds;
  });
  segments.push({ kind: "end", start: clock, duration: END_SECONDS });

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const maybeContext = canvas.getContext("2d");
  if (!maybeContext) throw new Error("Canvas isn't available in this browser.");
  const context = maybeContext;

  const stream = canvas.captureStream(FPS);

  // VO: route matched reads through WebAudio into the recording (and the speakers, so
  // the director hears the film as it's captured).
  const sceneVoiceUrls = Object.entries(script.voiceUrlsBySceneId).filter(([, urls]) => urls.length);
  let audioContext: AudioContext | null = null;
  const voiceElements: HTMLAudioElement[] = [];
  const voiceTimers: number[] = [];
  if (sceneVoiceUrls.length) {
    audioContext = new AudioContext();
    void audioContext.resume().catch(() => undefined);
    const destination = audioContext.createMediaStreamDestination();
    destination.stream.getAudioTracks().forEach((track) => stream.addTrack(track));

    const sceneStarts = new Map<string, number>();
    const sceneEnds = new Map<string, number>();
    for (const segment of segments) {
      if (segment.kind !== "board" || !segment.entry) continue;
      const id = segment.entry.sceneId;
      if (!sceneStarts.has(id)) sceneStarts.set(id, segment.start);
      sceneEnds.set(id, segment.start + segment.duration);
    }

    for (const [sceneId, urls] of sceneVoiceUrls) {
      const startAt = sceneStarts.get(sceneId);
      if (startAt === undefined) continue;
      const stopAt = sceneEnds.get(sceneId) ?? totalSeconds;
      const elements = urls.map((url) => {
        const element = new Audio(url);
        element.preload = "auto";
        const source = audioContext!.createMediaElementSource(element);
        source.connect(destination);
        source.connect(audioContext!.destination);
        voiceElements.push(element);
        return element;
      });
      voiceTimers.push(
        window.setTimeout(() => {
          let index = 0;
          const playNext = () => {
            const element = elements[index];
            if (!element) return;
            element.onended = () => {
              index += 1;
              playNext();
            };
            void element.play().catch(() => undefined);
          };
          playNext();
        }, startAt * 1000)
      );
      // Reads stop when their scene leaves the screen — same rule as Watch mode.
      voiceTimers.push(
        window.setTimeout(() => {
          elements.forEach((element) => {
            element.onended = null;
            element.pause();
          });
        }, stopAt * 1000)
      );
    }
  }

  const recorder = new MediaRecorder(stream, { mimeType: picked.mimeType, videoBitsPerSecond: 8_000_000 });
  const chunks: BlobPart[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size) chunks.push(event.data);
  };

  return new Promise<AnimaticResult>((resolve, reject) => {
    let frameHandle = 0;
    let progressTimer = 0;
    let finished = false;
    const startedAt = performance.now();

    function cleanup() {
      cancelAnimationFrame(frameHandle);
      window.clearInterval(progressTimer);
      voiceTimers.forEach((timer) => window.clearTimeout(timer));
      voiceElements.forEach((element) => {
        element.onended = null;
        element.pause();
      });
      void audioContext?.close().catch(() => undefined);
      stream.getTracks().forEach((track) => track.stop());
    }

    function finish(cancelled: boolean) {
      if (finished) return;
      finished = true;
      if (cancelled) {
        recorder.onstop = null;
        try {
          recorder.stop();
        } catch {
          // already stopped
        }
        cleanup();
        reject(new DOMException("Cancelled", "AbortError"));
        return;
      }
      try {
        recorder.stop();
      } catch {
        // already stopped
      }
    }

    recorder.onstop = () => {
      cleanup();
      resolve({ blob: new Blob(chunks, { type: picked.mimeType.split(";")[0] }), extension: picked.extension });
    };
    recorder.onerror = () => {
      cleanup();
      reject(new Error("Recording failed partway through."));
    };

    options.signal?.addEventListener("abort", () => finish(true), { once: true });

    function draw() {
      const elapsed = (performance.now() - startedAt) / 1000;
      if (elapsed >= totalSeconds) {
        finish(false);
        return;
      }
      const segment = segments.find((item) => elapsed >= item.start && elapsed < item.start + item.duration) || segments[segments.length - 1];
      context.fillStyle = "#06080d";
      context.fillRect(0, 0, width, height);
      if (segment.kind === "title") {
        drawCard(context, width, height, script.title, "A Kinetix previsualization", false);
      } else if (segment.kind === "end") {
        drawCard(context, width, height, script.title, "End of cut · nothing rendered yet", true);
      } else if (segment.entry) {
        if (segment.image) {
          drawBoard(context, segment.image, width, height, (elapsed - segment.start) / segment.duration);
        }
        if (segment.entry.caption) drawCaption(context, segment.entry.caption, width, height);
      }
      frameHandle = requestAnimationFrame(draw);
    }

    recorder.start(1000);
    progressTimer = window.setInterval(() => {
      const elapsed = Math.min(totalSeconds, (performance.now() - startedAt) / 1000);
      options.onProgress?.({ elapsedSeconds: elapsed, totalSeconds });
    }, 250);
    frameHandle = requestAnimationFrame(draw);
  });
}

/** Hand the finished film to the browser's downloads. */
export function downloadAnimatic(result: AnimaticResult, title: string) {
  const safeTitle = (title || "kinetix-film").replace(/[\\/:*?"<>|]+/g, "").trim() || "kinetix-film";
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${safeTitle} — animatic.${result.extension}`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
